// Individual press release page — reached via `/press-releases/:slug`.
// Renders the release body from the structured data (paragraph strings,
// { type: 'heading' } or { type: 'list' } blocks) so future releases can be
// authored without touching the component.

import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Phone, MapPinned, Newspaper, Copy } from 'lucide-react';
import { useState } from 'react';
import PageShell from '../components/PageShell';
import SEO from '../components/SEO';
import { JoySticker } from '../components/JoyAccents';
import { Button } from '../components/ui/button';
import { useI18n } from '../contexts/I18nContext';
import { getPressReleaseBySlug } from '../data/pressReleases';

export default function PressReleasePage() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [copied, setCopied] = useState(false);
  const release = getPressReleaseBySlug(slug);

  if (!release) {
    return <Navigate to="/press-releases" replace />;
  }

  // Prefer the body in the visitor's active language when the release provides
  // a translated variant; otherwise fall back to the English body.
  const localizedBody = release[`body_${lang}`] || release.body;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard unavailable — silently ignore.
    }
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: release.title,
    datePublished: release.date,
    dateModified: release.date,
    description: release.excerpt,
    author: { '@type': 'Organization', name: 'GEMS @ The Arts' },
    publisher: {
      '@type': 'Organization',
      name: 'GEMS @ The Arts',
      logo: { '@type': 'ImageObject', url: 'https://gemsatthearts.org/og-image.jpg' },
    },
    mainEntityOfPage: `https://gemsatthearts.org/press-releases/${release.slug}`,
  };

  return (
    <PageShell
      eyebrow={<><Newspaper className="h-4 w-4" /> {release.eyebrow || t('press.release.eyebrow')}</>}
      title={release.title}
      subtitle={release.excerpt}
      accent="coral"
    >
      <SEO
        title={release.title}
        description={release.excerpt}
        path={`/press-releases/${release.slug}`}
        jsonLd={jsonLd}
      />

      <section className="pb-8">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <Link
            to="/press-releases"
            className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#E5614C] hover:underline"
            data-testid="press-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t('press.backAll')}
          </Link>

          {/* Meta ribbon */}
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-[#2A1B1A]/70">
            <span className="inline-flex items-center gap-1.5 font-semibold text-[#2A1B1A]">
              <Calendar className="h-4 w-4 text-[#E5614C]" /> {release.dateLabel}
            </span>
            {release.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-[#5DCAB5]" /> {release.location}
              </span>
            )}
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 hover:text-[#E5614C] transition-colors"
              data-testid="press-copy-link"
            >
              <Copy className="h-3.5 w-3.5" /> {copied ? t('press.linkCopied') : t('press.copyLink')}
            </button>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="pb-14" data-testid="press-body">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <article className="relative bg-white rounded-[28px] border border-[#2A1B1A]/8 shadow-[0_30px_60px_-30px_rgba(42,27,26,0.2)] p-8 sm:p-12 lg:p-14">
            <div className="absolute -top-4 left-8">
              <JoySticker rotate={-3} color="yellow">
                <span className="inline-flex items-center gap-1.5">
                  <Newspaper className="h-3.5 w-3.5" /> {release.eyebrow || t('press.release.eyebrow')}
                </span>
              </JoySticker>
            </div>

            <div className="mt-4 prose prose-lg max-w-none">
              {localizedBody.map((block, i) => {
                if (typeof block === 'string') {
                  return (
                    <p key={i} className="text-[16px] sm:text-[17px] text-[#2A1B1A]/85 leading-[1.75] mb-5">
                      {block}
                    </p>
                  );
                }
                if (block.type === 'heading') {
                  return (
                    <h2 key={i} className="mt-8 mb-3 font-serif-display text-[22px] sm:text-[26px] font-bold text-[#2A1B1A] leading-snug">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'list') {
                  return (
                    <ul key={i} className="mb-6 space-y-2.5 pl-0">
                      {block.items.map((it, j) => (
                        <li key={j} className="pl-5 relative text-[16px] text-[#2A1B1A]/85 leading-relaxed">
                          <span className="absolute left-0 top-[10px] h-1.5 w-1.5 rounded-full bg-[#E5614C]" aria-hidden="true" />
                          {it}
                        </li>
                      ))}
                    </ul>
                  );
                }
                return null;
              })}
            </div>

            {/* Sign-off */}
            <div className="mt-10 pt-6 border-t border-[#2A1B1A]/10 text-center">
              <div className="text-[24px] font-bold text-[#2A1B1A]/40 tracking-[0.4em]">###</div>
            </div>
          </article>
        </div>
      </section>

      {/* Contact card */}
      {release.contact && (
        <section className="pb-24" data-testid="press-contact">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <div className="rounded-[24px] bg-[#2A1B1A] text-white p-8 sm:p-10 lg:p-12 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-[#E5614C]/15 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-[#FCD968] font-semibold uppercase tracking-[0.18em] text-[12px]">
                  <span className="h-[2px] w-8 bg-[#FCD968]" /> {t('press.mediaContact')}
                </div>
                <h3 className="mt-4 font-serif-display text-[24px] sm:text-[30px] font-bold leading-tight">
                  {release.contact.org}
                </h3>
                <div className="mt-5 grid sm:grid-cols-2 gap-4 text-[14.5px]">
                  <div className="flex items-start gap-2.5">
                    <MapPinned className="h-4 w-4 mt-0.5 text-[#5DCAB5] shrink-0" />
                    <span className="text-white/85">{release.contact.address}</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Phone className="h-4 w-4 mt-0.5 text-[#5DCAB5] shrink-0" />
                    <a href={`tel:${release.contact.phone.replace(/\D/g, '')}`} className="text-white/85 hover:text-[#FCD968]">
                      {release.contact.phone}
                    </a>
                  </div>
                </div>
                <div className="mt-6">
                  <Button
                    asChild
                    className="rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-11 px-6 btn-press"
                    data-testid="press-contact-email"
                  >
                    <a href={`mailto:${release.contact.email}`}>{t('press.contact.button')}</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}
